import React from 'react'
import { numericSection } from '../utils/sectioned-input/numeric-section'
import { BuildUseSectionedInput } from '../utils/sectioned-input/use-sectioned-input'
import { composeEventHandlers } from '../utils/compose-event-handlers'
import { YearMonthFns } from '../utils/datetime'

export interface HeadlessMonthDayInputProps extends Omit<
  React.ComponentPropsWithoutRef<'input'>,
  'value' | 'defaultValue' | 'onChange'
> {
  value?: string | null
  defaultValue?: string | null
  onChange?(value: string | null): unknown
}

export const HeadlessMonthDayInput = React.forwardRef<HTMLInputElement, HeadlessMonthDayInputProps>(
  ({ value, defaultValue, onChange, ...props }, forwardedRef) => {
    const { inputProps } = useMonthDayInput({
      value,
      defaultValue,
      onChange
    })
    return (
      <input
        {...props}
        {...inputProps}
        onKeyDown={composeEventHandlers(inputProps.onKeyDown, props.onKeyDown)}
        onPaste={composeEventHandlers(inputProps.onPaste, props.onPaste)}
        onMouseUp={composeEventHandlers(inputProps.onMouseUp, props.onMouseUp)}
        onSelect={composeEventHandlers(inputProps.onSelect, props.onSelect)}
        ref={forwardedRef}
      />
    )
  }
)
HeadlessMonthDayInput.displayName = 'HeadlessMonthDayInput'

const useMonthDayInput = BuildUseSectionedInput({
  sections: [
    numericSection({ min: 1, max: 12, placeholder: 'MM', suffix: ' / ', pad: true }),
    numericSection({
      min: 1,
      max: 31,
      placeholder: 'DD',
      suffix: '',
      pad: true,
      tick(state, { sectionValues, action }) {
        const [monthStr, dayStr] = sectionValues
        const month = Number(monthStr)
        const day = Number(dayStr)
        const daysInMonth = YearMonthFns.getDaysInMonth(`1972-${`${month}`.padStart(2, '0')}`)
        if (Number.isNaN(daysInMonth) || Number.isNaN(day) || day <= daysInMonth) {
          return sectionValues[1]
        } else {
          if (action === 'increment') {
            return `01`
          } else {
            return `${daysInMonth}`.padStart(2, '0')
          }
        }
      }
    })
  ],
  externalValueToSectionValues(value) {
    if (!value) {
      return ['MM', 'DD']
    }
    const [monthStr, dayStr] = value.split('-').filter(Boolean)
    return [monthStr, dayStr]
  },
  sectionValuesToExternalValue(sectionValues) {
    const [monthStr, dayStr, yearStr] = sectionValues
    const month = Number(monthStr)
    const daysInMonth = YearMonthFns.getDaysInMonth(`1972-${`${month}`.padStart(2, '0')}`)
    const day = Math.min(Number(dayStr), Number.isNaN(daysInMonth) ? 50 : daysInMonth)
    const date = new Date(Date.UTC(1972, month - 1, day))
    const invalid = Number.isNaN(date.getTime())
    if (invalid) {
      return null
    } else {
      return `--${date.toISOString().slice(5, 10)}`
    }
  },
  tryParsePaste(value) {
    try {
      const date = new Date(value)
      if (!Number.isNaN(date.getTime())) {
        return [`${date.getMonth() + 1}`.padStart(2, '0'), `${date.getDate()}`.padStart(2, '0')]
      } else {
        return null
      }
    } catch {
      return null
    }
  }
})
